// The signed-in frame every product shares: ink rail with the brand and the
// nav, a top bar, a pill nav on phones, and the user menu. What goes in the
// nav and in the top bar is the product's business, passed in as props.

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { cn } from '../lib/cn'
import { useI18n } from '../i18n/LocaleContext'
import { useSession } from '../session/useSession'
import { initials, memberLabel } from '../hooks/useMembers'
import { Logo } from '../ui/Logo'
import { ChevronDownIcon, SettingsIcon } from '../ui/icons'

export type NavItem = {
  to: string
  label: string
  icon: ReactNode
  /** Match the path exactly (the home item), not as a prefix. */
  end?: boolean
}

export function AppShell({
  nav,
  headerStart,
  headerEnd,
}: {
  nav: NavItem[]
  /** Left of the top bar, e.g. a search affordance. Takes the free width. */
  headerStart?: ReactNode
  /** Right of the top bar, before the user menu, e.g. the primary action. */
  headerEnd?: ReactNode
}) {
  const session = useSession()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useI18n()

  const name =
    session.status === 'authenticated'
      ? memberLabel({ full_name: session.profile.full_name, email: session.profile.email })
      : ''
  const email = session.status === 'authenticated' ? session.profile.email : ''

  async function signOut() {
    await supabase.auth.signOut()
    navigate('/login', { replace: true })
  }

  return (
    <div className="flex min-h-screen bg-canvas print:block">
      {/* ── Ink rail ───────────────────────────────────────────────────────── */}
      <aside className="no-print sticky top-0 hidden h-screen w-[276px] shrink-0 flex-col gap-7 bg-ink p-4 md:flex">
        <Link to="/" className="flex items-center px-2" aria-label={t.brand.name}>
          <Logo size={40} onDark />
        </Link>

        <nav className="flex flex-col gap-1">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-[15px] font-semibold transition-colors',
                  isActive ? 'bg-gray-50/15 text-gray-50' : 'text-gray-200 hover:bg-gray-50/10',
                )
              }
            >
              <span className="shrink-0">{item.icon}</span>
              <span className="truncate">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto">
          <UserMenu name={name} email={email} onSignOut={signOut} onDark />
        </div>
      </aside>

      {/* ── Content column ─────────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print sticky top-0 z-30 flex h-14 shrink-0 items-center gap-3 border-b border-DEFAULT bg-canvas/90 px-4 backdrop-blur sm:px-5">
          <Link to="/" className="flex items-center md:hidden" aria-label={t.brand.name}>
            <Logo size={32} />
          </Link>

          {headerStart ?? <span className="flex-1" />}

          <div className="ml-auto flex items-center gap-2.5">
            {headerEnd}
            <span className="md:hidden">
              <UserMenu name={name} email={email} onSignOut={signOut} />
            </span>
          </div>
        </header>

        {/* Mobile nav: the rail is hidden under 768px. */}
        <nav className="no-print flex gap-1.5 overflow-x-auto px-4 py-2.5 md:hidden">
          {nav.map((item) => {
            const active = item.end
              ? location.pathname === item.to
              : location.pathname.startsWith(item.to)
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  'flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[13px] font-semibold transition-colors',
                  active
                    ? 'border-ink bg-ink text-sage-50'
                    : 'border-DEFAULT text-gray-700 hover:bg-gray-900/[0.07]',
                )}
              >
                {item.icon}
                {item.label}
              </Link>
            )
          })}
        </nav>

        <main className="mx-auto w-full max-w-[1240px] flex-1 px-4 py-6 sm:px-9 sm:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

function UserMenu({
  name,
  email,
  onSignOut,
  onDark,
}: {
  name: string
  email: string
  onSignOut: () => void
  /** Sitting on the ink rail: light text, popover opens upward. */
  onDark?: boolean
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onPointerDown(e: PointerEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'flex items-center gap-2.5 rounded-full transition-colors',
          onDark
            ? cn('w-full px-2 py-1.5 text-left hover:bg-gray-50/10', open && 'bg-gray-50/10')
            : cn('py-0.5 pl-0.5 pr-1.5 hover:bg-gray-900/[0.07]', open && 'bg-gray-900/[0.07]'),
        )}
      >
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full font-display',
            onDark
              ? 'h-[38px] w-[38px] bg-sage-700 text-[14px] text-sage-50'
              : 'h-8 w-8 bg-brand-500 text-[12px] text-gray-50',
          )}
        >
          {initials(name)}
        </span>
        {onDark && (
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[14px] font-semibold text-gray-50">{name}</span>
            <span className="block truncate text-[12px] text-gray-400">{email}</span>
          </span>
        )}
        <span
          className={cn(
            'transition-transform',
            onDark ? 'text-gray-400' : 'text-gray-500',
            open && 'rotate-180',
          )}
        >
          <ChevronDownIcon size={15} />
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className={cn(
            'absolute z-40 w-60 overflow-hidden rounded-lg bg-surface shadow-pop',
            onDark ? 'bottom-full left-0 mb-2' : 'right-0 top-full mt-2',
          )}
        >
          <div className="border-b border-DEFAULT px-4 py-3">
            <p className="truncate text-[14px] font-semibold text-gray-900">{name}</p>
            <p className="truncate text-[12.5px] text-gray-500">{email}</p>
          </div>
          <Link
            to="/settings"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-[13.5px] text-gray-700 hover:bg-gray-900/[0.07]"
          >
            <SettingsIcon size={15} />
            {t.nav.settings}
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              void onSignOut()
            }}
            className="flex w-full items-center gap-2 border-t border-DEFAULT px-4 py-2.5 text-left text-[13.5px] text-gray-700 hover:bg-gray-900/[0.07]"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <path d="m16 17 5-5-5-5M21 12H9" />
            </svg>
            {t.auth.signOut}
          </button>
        </div>
      )}
    </div>
  )
}
